/**
 * Master — Tarif Borongan Jahit per (Wilayah Produksi × Jenis Pekerjaan) (K61-K70, K73, K78, K81, K84, K91).
 *
 * ⚠ TATA ULANG 2 Nov 2026 (T110 + T111, Owner: "benahin dulu aja bro biar tidak membingungkan"). Owner mengubah
 * tarif Surabaya · Jahit Atasan 12.000 → 8.000 (tersimpan benar di database) tetapi tidak melihatnya: tabel "Tarif
 * Berlaku Sekarang" ada di DASAR halaman, di bawah daftar komponen + status kerah + ambang, dan riwayatnya dialog
 * terpisah yang dimuat sekali saat dibuka. Bentuk sekarang:
 *   - SATU KARTU PER WILAYAH di atas: jenis pekerjaan × [tarif berlaku sekarang · versi terjadwal berikutnya ·
 *     terakhir diubah · tombol "Ubah tarif" yang sudah terisi wilayah + jenis]; riwayat = garis waktu yang dibuka
 *     di baris itu (terbaru di atas); contoh hitung "1 jersey set (atasan + celana)".
 *   - Sesudah simpan: daftar DAN riwayat kombinasi itu dimuat ulang dan dibuka, versi baru ditandai — tanpa muat
 *     ulang halaman. "Berlaku sekarang/terjadwal" memakai tanggal CABANG dari server (`hari_ini_wilayah`,
 *     `belum_berlaku`), bukan jam peramban.
 *   - Dialog ubah: ringkasan "RpX → RpY (naik/turun Z%), berlaku untuk FA yang dibuat mulai …; FA sebelumnya tetap
 *     tarif lama", peringatan bila berubah lebih dari 20% (salah ketik nol), lalu konfirmasi ulang.
 *   - T110: jenis pekerjaan uji (kode "UJI-…", K84) DISEMBUNYIKAN dari layar dan pilihan dialog. Barisnya tetap
 *     permanen di database (K84); PRODUCTION tetap ditolak selama masih ada yang aktif (TARIF_TANPA_BARIS_UJI).
 *     Ini menggantikan penanda "DATA UJI" 24 Agustus 2026 — keputusan tampilan, bukan pelonggaran K84.
 *
 * Yang TIDAK berubah (aturan bisnis): K62 tarif berversi — mengubah = INSERT versi baru (POST), tidak ada
 * PATCH/PUT; K64 tanggal tidak boleh mundur (date-picker `min` = hari ini cabang, server + trigger penjaganya);
 * K78 perubahan Kepala Cabang di atas ambang dijawab 202 dan menunggu Owner; K68 FINANCE baca saja — seluruh
 * kontrol ubah TIDAK ter-mount untuknya; K91 baris pra-K91 dibedakan gaya (penanda, bukan alasan siapa pun);
 * UAT7-T3 nama pengubah dari `updated_by_name`, UUID mentah tidak pernah dirender.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "../components/ui/table";
import { StatusBadge } from "../components/etams/StatusBadge";
import { Plus, Info, History, ChevronUp, AlertTriangle, Pencil } from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDate, formatDateTime } from "../lib/format";
import PersetujuanTarifBorongan from "../components/etams/PersetujuanTarifBorongan";
import MasterKomponenBorongan from "../components/etams/MasterKomponenBorongan";
import Hint from "../components/etams/Hint";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, Paginasi } from "../components/etams/PalangDaftar";
import {
  LABEL_KOMPONEN, LABEL_STATUS_RIWAYAT, barisTarif, contohJerseySet, dataUji, kelompokWilayah, kunciKombinasi,
  ringkasanUbahTarif, statusRiwayat,
} from "../lib/tarifBorongan";

const ENDPOINT = "/master/sewing-rates";
const ENDPOINT_RIWAYAT = `${ENDPOINT}/riwayat`;
const ENDPOINT_WORK_TYPES = "/master/sewing-work-types";
const ENDPOINT_AMBANG = "/master/sewing-rate-settings";

// K91 — baris PRA-K91 membawa PENANDA dari migration `20260824120000`, bukan alasan dari siapa pun.
const PRA_K91_PREFIX = "(PRA-K91)";

function AlasanTeks({ reason }) {
  if (!reason) return "—";
  if (reason.startsWith(PRA_K91_PREFIX)) {
    return (
      <span className="italic text-muted-foreground">
        <Info className="h-3 w-3 inline-block align-text-bottom mr-1" />
        {`(data lama)${reason.slice(PRA_K91_PREFIX.length)}`}
      </span>
    );
  }
  return reason;
}

/** UAT7-T3: nama pengubah; kosong = "tidak tercatat" (baris pra-K91 / akun terhapus), bukan UUID. */
function namaPengubah(v) {
  return v?.updated_by_name || "(tidak tercatat)";
}

function pesanBackend(e) {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object") return d.message || null;
  return typeof d === "string" ? d : null;
}

function pesanGagalMuat(e, cadangan) {
  return pesanBackend(e) || (e?.response?.status === 403 ? "Anda tidak punya hak melihat data ini." : cadangan);
}

function pesanGagalSimpan(e) {
  const status = e?.response?.status;
  const dariServer = pesanBackend(e);
  if (dariServer) return dariServer;
  if (status === 403) return "Anda tidak punya hak mengubah tarif untuk wilayah ini.";
  if (status === 409) {
    return "Sudah ada versi tarif dengan tanggal berlaku yang sama untuk wilayah + jenis pekerjaan ini. "
      + "Pilih tanggal lain, atau periksa riwayatnya dulu.";
  }
  if (status === 422) return "Isian belum benar — periksa kembali wilayah, jenis pekerjaan, tarif, dan tanggal.";
  return "Tarif belum tersimpan.";
}

function hariIniPeramban() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Garis waktu riwayat SATU kombinasi — terbaru di atas; versi yang baru disimpan ditandai. */
function GarisWaktu({ isi, baruId }) {
  if (!isi || (isi.memuat && !isi.items)) {
    return <p className="text-sm text-muted-foreground">Memuat riwayat…</p>;
  }
  const versi = statusRiwayat(isi.items || []);
  if (versi.length === 0) return <p className="text-sm text-muted-foreground">Belum ada riwayat.</p>;
  return (
    <ol className="space-y-3 border-l border-border pl-4 ml-1" data-testid="garis-waktu-riwayat">
      {versi.map((v) => {
        const st = LABEL_STATUS_RIWAYAT[v.status];
        return (
          <li key={v.id} className="relative" data-testid={`riwayat-${v.id}`}>
            <span className={"absolute -left-[1.4rem] top-1.5 h-2.5 w-2.5 rounded-full "
              + (v.status === "AKTIF" ? "bg-primary" : "bg-muted-foreground/40")} />
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-mono font-semibold">{formatIDR(v.rate_per_pcs_idr)}</span>
              <StatusBadge tone={st.tone}>{st.l}</StatusBadge>
              <span className="text-muted-foreground">berlaku sejak {formatDate(v.berlaku_sejak)}</span>
              {v.id === baruId && <StatusBadge tone="success" testId="penanda-baru">Baru disimpan</StatusBadge>}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              oleh {namaPengubah(v)} · disimpan {formatDateTime(v.created_at)}
            </div>
            <div className="text-sm mt-0.5 whitespace-normal break-words">
              Alasan: <AlasanTeks reason={v.reason} />
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export default function MasterSewingRates() {
  const { isOwner, isKepalaCabang, roleCode } = useAuth();
  const bolehTulis = isOwner || isKepalaCabang; // K61 — FINANCE selalu baca saja (K68).

  const [data, setData] = useState({ items: [], kosong: [], hari: {} });
  const [loading, setLoading] = useState(true);
  const sudahDimuat = useRef(false);
  const [workTypes, setWorkTypes] = useState([]);
  const [ambang, setAmbang] = useState({});
  const [muatUlangAntrean, setMuatUlangAntrean] = useState(0);

  const [terbuka, setTerbuka] = useState({});       // kunci kombinasi → riwayat terbuka
  const [riwayat, setRiwayat] = useState({});       // kunci kombinasi → { memuat, items }
  const [baruId, setBaruId] = useState(null);

  const [dialog, setDialog] = useState(null);       // { tetap: bool } — tetap = wilayah+jenis dari baris
  const [form, setForm] = useState({});
  const [langkah, setLangkah] = useState("ISI");    // ISI → PERIKSA (konfirmasi ulang)
  const [yakin, setYakin] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(ENDPOINT);
      setData({
        items: r.data.items || [],
        kosong: r.data.kombinasi_tanpa_tarif || [],
        hari: r.data.hari_ini_wilayah || {},
      });
      sudahDimuat.current = true;
    } catch (e) {
      toast.error(pesanGagalMuat(e, "Gagal memuat tarif borongan"));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadWorkTypes = useCallback(async () => {
    try {
      const r = await api.get(ENDPOINT_WORK_TYPES);
      setWorkTypes(r.data.items || []);
    } catch (e) {
      toast.error(pesanGagalMuat(e, "Gagal memuat jenis pekerjaan"));
    }
  }, []);

  const loadAmbang = useCallback(async () => {
    try {
      const r = await api.get(ENDPOINT_AMBANG);
      setAmbang(Object.fromEntries((r.data.items || []).map((a) => [a.region_id, Number(a.pct_threshold)])));
    } catch {
      setAmbang({}); // hanya untuk kalimat "akan menunggu Owner" di dialog — kartu ambang memuat sendiri
    }
  }, []);

  useEffect(() => { load(); loadWorkTypes(); loadAmbang(); }, [load, loadWorkTypes, loadAmbang]);

  const muatRiwayat = useCallback(async (row) => {
    const kunci = kunciKombinasi(row);
    setRiwayat((m) => ({ ...m, [kunci]: { ...(m[kunci] || {}), memuat: true } }));
    try {
      const r = await api.get(ENDPOINT_RIWAYAT, {
        params: { region_id: row.region_id, work_type_id: row.work_type_id },
      });
      setRiwayat((m) => ({ ...m, [kunci]: { memuat: false, items: r.data.items || [] } }));
    } catch (e) {
      toast.error(pesanGagalMuat(e, "Gagal memuat riwayat tarif"));
      setRiwayat((m) => ({ ...m, [kunci]: { memuat: false, items: m[kunci]?.items || [] } }));
    }
  }, []);

  const toggleRiwayat = (row) => {
    const kunci = kunciKombinasi(row);
    const buka = !terbuka[kunci];
    setTerbuka((m) => ({ ...m, [kunci]: buka }));
    if (buka) muatRiwayat(row);
  };

  // T110 — data uji tidak ikut; T111 — kombinasi tanpa tarif ikut sebagai baris di kartu wilayahnya.
  const semuaBaris = useMemo(() => barisTarif(data.items, data.kosong), [data]);
  const jumlahUji = useMemo(() => new Set([...data.items, ...data.kosong].filter(dataUji)
    .map((r) => r.work_type_id)).size, [data]);
  const jumlahKosong = semuaBaris.filter((r) => !r.ada).length;

  // Pola daftar seragam (28 Sep 2026): master KECIL, cari/halaman di klien; kartu wilayah dibentuk dari halaman ini.
  const daftar = useDaftar(semuaBaris, {
    kunciCari: [(r) => r.region_name, (r) => r.work_type_name],
    ukuran: 50,
    kolom: [
      { kunci: "wilayah", judul: "Wilayah", ambil: (r) => r.region_name },
      { kunci: "jenis", judul: "Jenis Pekerjaan", ambil: (r) => r.work_type_name },
      { kunci: "rate_per_pcs_idr", judul: "Tarif berlaku (IDR)" },
      { kunci: "berlaku_sejak", judul: "Berlaku sejak" },
      { kunci: "berikutnya", judul: "Versi berikutnya (IDR)", ambil: (r) => r.versi_berikutnya?.rate_per_pcs_idr ?? "" },
      { kunci: "berikutnya_sejak", judul: "Versi berikutnya mulai", ambil: (r) => r.versi_berikutnya?.berlaku_sejak ?? "" },
      { kunci: "reason", judul: "Alasan" },
    ],
  });
  const kartu = useMemo(() => kelompokWilayah(daftar.tampil), [daftar.tampil]);

  const wilayahPilihan = useMemo(() => kelompokWilayah(semuaBaris)
    .map((g) => ({ id: g.region_id, name: g.region_name })), [semuaBaris]);
  // T110: pilihan dialog tanpa jenis uji dan tanpa jenis nonaktif (server menolak WORK_TYPE_NONAKTIF).
  const jenisPilihan = useMemo(() => workTypes.filter((w) => !dataUji(w) && w.is_active !== false), [workTypes]);

  const barisDari = (regionId, workTypeId) => semuaBaris.find(
    (r) => r.region_id === regionId && r.work_type_id === workTypeId);

  const bukaUbah = (row) => {
    setForm({
      region_id: row?.region_id || "",
      work_type_id: row?.work_type_id || "",
      berlaku_sejak: (row && data.hari[row.region_id]) || hariIniPeramban(),
    });
    setLangkah("ISI");
    setYakin(false);
    setDialog({ tetap: !!row });
  };

  const hariIniForm = (form.region_id && data.hari[form.region_id]) || hariIniPeramban();
  const barisForm = form.region_id && form.work_type_id ? barisDari(form.region_id, form.work_type_id) : null;
  const namaWilayahForm = barisForm?.region_name
    || wilayahPilihan.find((w) => String(w.id) === String(form.region_id))?.name;
  const namaJenisForm = barisForm?.work_type_name
    || jenisPilihan.find((w) => String(w.id) === String(form.work_type_id))?.name;
  const rateBaru = Number(form.rate_per_pcs_idr);
  const ringkasan = Number.isInteger(rateBaru) && rateBaru > 0 && form.berlaku_sejak
    ? ringkasanUbahTarif({
      wilayah: namaWilayahForm, jenis: namaJenisForm,
      lama: barisForm?.ada ? barisForm.rate_per_pcs_idr : null, baru: rateBaru, sejak: form.berlaku_sejak,
    })
    : null;
  const ambangForm = ambang[form.region_id];
  const akanMenungguOwner = !isOwner && ringkasan && (ringkasan.arah === "PERTAMA"
    || (Number.isFinite(ambangForm) && Math.abs(ringkasan.persen) > ambangForm));

  const periksa = () => {
    // Kenyamanan, bukan pagar: server + trigger K64 tetap penjaga sebenarnya.
    if (!form.region_id) return toast.error("Pilih wilayah produksi");
    if (!form.work_type_id) return toast.error("Pilih jenis pekerjaan");
    if (!Number.isInteger(rateBaru) || rateBaru <= 0) {
      return toast.error("Tarif per potong harus bilangan bulat rupiah, lebih dari 0");
    }
    if (!form.berlaku_sejak) return toast.error("Tanggal berlaku sejak wajib diisi");
    if (form.berlaku_sejak < hariIniForm) return toast.error("Tanggal berlaku sejak tidak boleh mundur dari hari ini");
    if (!form.reason || !form.reason.trim()) return toast.error("Alasan perubahan wajib diisi");
    setYakin(false);
    setLangkah("PERIKSA");
    return null;
  };

  const simpan = async () => {
    if (ringkasan?.besar && !yakin) return toast.error("Centang dulu bahwa angkanya sudah diperiksa.");
    setSaving(true);
    const row = { region_id: form.region_id, work_type_id: form.work_type_id };
    try {
      const res = await api.post(ENDPOINT, {
        region_id: form.region_id,
        work_type_id: form.work_type_id,
        rate_per_pcs_idr: rateBaru,
        berlaku_sejak: form.berlaku_sejak,
        reason: form.reason.trim(),
      });
      // K78 (c): di atas ambang → HTTP 202 + status PENDING; tarif lama tetap berlaku sampai Owner memutus.
      const pending = res.status === 202 || res.data?.status === "PENDING_APPROVAL" || res.data?.status === "PENDING";
      if (pending) {
        toast.info(res.data?.message || "Perubahan di atas ambang — menunggu persetujuan Owner.");
        setBaruId(null);
      } else {
        toast.success("Tarif baru tersimpan.");
        setBaruId(res.data?.id || null);
      }
      setDialog(null);
      setForm({});
      setMuatUlangAntrean((n) => n + 1);
      // T111a: daftar DAN riwayat kombinasi ini dimuat ulang dan dibuka — versi baru langsung terlihat.
      setTerbuka((m) => ({ ...m, [kunciKombinasi(row)]: true }));
      await Promise.all([load(), muatRiwayat(row)]);
    } catch (e) {
      toast.error(pesanGagalSimpan(e));
    } finally {
      setSaving(false);
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="master-sewing-rate-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">
            Tarif Borongan{" "}
            <Hint>Upah jahit per potong yang dibayar ke penjahit (tahap Jahit saja; tahap lain digaji tetap). Satu tarif per wilayah produksi × jenis pekerjaan; Balikpapan memakai tarif Samarinda.</Hint>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Upah jahit per potong yang dibayar ke penjahit — bukan harga jual ke customer. Tarif dikunci menurut
            tanggal FA dibuat.
          </p>
        </div>
        {bolehTulis && (
          <Button variant="outline" onClick={() => bukaUbah(null)} data-testid="btn-tambah-versi">
            <Plus className="h-4 w-4 mr-1" /> Tarif jenis lain
          </Button>
        )}
      </div>

      {roleCode === "FINANCE" && (
        <p className="text-sm text-muted-foreground" data-testid="catatan-baca-finance">
          Anda melihat tarif seluruh cabang dalam mode baca.{" "}
          <Hint>Hanya Owner dan Kepala Cabang terkait yang mengubah tarif.</Hint>
        </p>
      )}

      {/* Antrean yang menunggu Owner tampil di ATAS — pengajuan Kepala Cabang di atas ambang belum berlaku. */}
      <PersetujuanTarifBorongan isOwner={isOwner} onBerubah={load} muatUlangKe={muatUlangAntrean} bagian="antrean" />

      <section className="space-y-3" data-testid="tarif-per-wilayah">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-medium">Tarif dasar &amp; tambahan per wilayah</h2>
            <p className="text-sm text-muted-foreground">
              Angka besar = tarif yang berlaku hari ini menurut tanggal cabang. Buka <b>Riwayat</b> di baris mana pun
              untuk melihat semua versinya.
            </p>
          </div>
          {!loading && semuaBaris.length > 0 && (
            <div className="w-full sm:w-auto">
              <PalangDaftar daftar={daftar} placeholder="Cari wilayah / jenis pekerjaan…" testId="tarif-palang"
                            ekspor={{ nama: "tarif_borongan", kolom: daftar.kolom }} />
            </div>
          )}
        </div>

        {jumlahKosong > 0 && (
          <p className="text-sm text-warning-soft-foreground" data-testid="kombinasi-tanpa-tarif">
            <AlertTriangle className="h-4 w-4 inline-block align-text-bottom mr-1" />
            {jumlahKosong} jenis pekerjaan belum punya tarif di wilayahnya — ditandai "Belum ada tarif" di bawah.
            Serah terima Jahit untuk jenis itu akan ditolak sampai tarifnya diisi.
          </p>
        )}

        {loading ? (
          <Card className="p-6 text-sm text-muted-foreground">Memuat…</Card>
        ) : semuaBaris.length === 0 ? (
          <Card className="p-6 text-sm text-muted-foreground">
            {bolehTulis
              ? "Belum ada jenis pekerjaan bertarif untuk wilayah Anda. Tambahkan komponen di bagian Komponen upah borongan di bawah."
              : "Belum ada tarif yang bisa Anda lihat. Kepala Cabang hanya melihat tarif cabang produksi sendiri."}
          </Card>
        ) : (
          <>
            {daftar.total === 0 && (
              <Card className="p-6 text-sm text-muted-foreground text-center">Tidak ada yang cocok.</Card>
            )}
            {kartu.map((g) => {
              const contoh = contohJerseySet(semuaBaris.filter((r) => r.region_id === g.region_id));
              return (
                <Card key={g.region_id} className="p-0 overflow-hidden" data-testid={`kartu-wilayah-${g.region_id}`}>
                  <div className="px-4 pt-4 pb-3 border-b border-border">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h3 className="text-base font-semibold">{g.region_name || "—"}</h3>
                      <span className="text-xs text-muted-foreground">
                        Hari ini di cabang: {formatDate(data.hari[g.region_id])}
                      </span>
                    </div>
                    <p className={"text-sm mt-1 " + (contoh.lengkap ? "" : "text-muted-foreground")}
                       data-testid={`contoh-hitung-${g.region_id}`}>
                      {contoh.kalimat}
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <Table data-testid={`tabel-tarif-${g.region_id}`}>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Jenis pekerjaan</TableHead>
                          <TableHead className="text-right">Tarif berlaku sekarang</TableHead>
                          <TableHead>Versi terjadwal berikutnya</TableHead>
                          <TableHead>Terakhir diubah</TableHead>
                          <TableHead />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {g.baris.map((row) => {
                          const kunci = kunciKombinasi(row);
                          const lanjut = row.versi_berikutnya;
                          return (
                            <React.Fragment key={kunci}>
                              <TableRow data-testid={`tarif-${kunci}`} className="align-top">
                                <TableCell>
                                  <div className="font-medium">{row.work_type_name || "—"}</div>
                                  {LABEL_KOMPONEN[row.jenis_komponen] && (
                                    <div className="text-xs text-muted-foreground">{LABEL_KOMPONEN[row.jenis_komponen]}</div>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  {row.ada ? (
                                    <>
                                      <div className="text-xl font-semibold font-mono" data-testid={`tarif-kini-${kunci}`}>
                                        {formatIDR(row.rate_per_pcs_idr)}
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        per potong · sejak {formatDate(row.berlaku_sejak)}
                                      </div>
                                    </>
                                  ) : (
                                    <StatusBadge tone="warning" testId={`tarif-kosong-${kunci}`}>Belum ada tarif</StatusBadge>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {lanjut ? (
                                    <div className="text-sm" data-testid={`terjadwal-${kunci}`}>
                                      <span className="font-mono font-medium">{formatIDR(lanjut.rate_per_pcs_idr)}</span>
                                      {" "}mulai {formatDate(lanjut.berlaku_sejak)}
                                      <div className="text-xs text-muted-foreground">
                                        oleh {namaPengubah(lanjut)}
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-sm text-muted-foreground">Tidak ada</span>
                                  )}
                                </TableCell>
                                <TableCell className="max-w-xs">
                                  {row.ada ? (
                                    <div className="text-sm">
                                      <div>{namaPengubah(row)} · {formatDate(row.created_at)}</div>
                                      <div className="text-xs text-muted-foreground line-clamp-2" title={row.reason || ""}>
                                        <AlasanTeks reason={row.reason} />
                                      </div>
                                    </div>
                                  ) : "—"}
                                </TableCell>
                                <TableCell className="whitespace-nowrap text-right">
                                  {bolehTulis && (
                                    <Button size="sm" variant={row.ada ? "outline" : "default"} onClick={() => bukaUbah(row)}
                                            data-testid={`btn-ubah-${kunci}`}>
                                      <Pencil className="h-3.5 w-3.5 mr-1" />{row.ada ? "Ubah tarif" : "Isi tarif"}
                                    </Button>
                                  )}
                                  <Button size="sm" variant="ghost" className="ml-1" onClick={() => toggleRiwayat(row)}
                                          aria-expanded={!!terbuka[kunci]} data-testid={`btn-riwayat-${kunci}`}>
                                    {terbuka[kunci]
                                      ? <><ChevronUp className="h-4 w-4 mr-1" />Tutup</>
                                      : <><History className="h-4 w-4 mr-1" />Riwayat</>}
                                  </Button>
                                </TableCell>
                              </TableRow>
                              {terbuka[kunci] && (
                                <TableRow className="bg-muted/40 hover:bg-muted/40" data-testid={`baris-riwayat-${kunci}`}>
                                  <TableCell colSpan={5} className="py-3">
                                    <GarisWaktu isi={riwayat[kunci]} baruId={baruId} />
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </Card>
              );
            })}
            <Paginasi daftar={daftar} testId="tarif-paginasi" />
          </>
        )}
        {isOwner && jumlahUji > 0 && (
          <p className="text-xs text-muted-foreground" data-testid="catatan-data-uji">
            {jumlahUji} jenis pekerjaan uji sistem tidak ditampilkan. Jenis itu tidak dipakai produksi, dan
            mode Produksi tidak bisa dinyalakan selama masih ada yang aktif.
          </p>
        )}
      </section>

      {/* VONIS-24SEP-BORONGAN: master KOMPONEN upah + status kerah per wilayah (tambahan UPAH per kerah). */}
      <MasterKomponenBorongan bolehTulis={isOwner} onBerubah={() => { load(); loadWorkTypes(); }} />

      <PersetujuanTarifBorongan isOwner={isOwner} onBerubah={load} muatUlangKe={muatUlangAntrean} bagian="ambang" />

      {/* Banner HPP tanpa overhead (K139 butir 1 — mencabut overhead K69 dan pembelahan sementara/final K70) —
          permanen, di dasar halaman sebagai catatan. */}
      <Card className="p-4 border-warning-soft-border" data-testid="peringatan-hpp-sementara">
        <div className="flex gap-3">
          <Info className="h-5 w-5 text-warning-soft-foreground shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">
            Tarif di layar ini adalah komponen upah jahit HPP.
            HPP resmi = biaya <strong>LANGSUNG</strong> (material + upah
            jahit, tanpa overhead) dan kini tersedia di layar
            <strong> HPP &amp; Margin</strong> (Owner). Biaya penuh perusahaan
            tetap lebih tinggi dari angka itu — margin yang dihitung darinya
            margin KOTOR.
          </p>
        </div>
      </Card>

      {/* K68 — seluruh subtree ubah TIDAK ADA di DOM untuk FINANCE (bukan cuma disembunyikan). */}
      {bolehTulis && (
      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialog?.tetap ? `Ubah tarif — ${namaWilayahForm || "—"} · ${namaJenisForm || "—"}` : "Tarif jenis pekerjaan"}
            </DialogTitle>
          </DialogHeader>

          {langkah === "ISI" ? (
            <div className="space-y-4">
              {!dialog?.tetap && (
                <>
                  <div>
                    <Label htmlFor="region_id">Wilayah produksi</Label>
                    <Select value={form.region_id || ""}
                            onValueChange={(v) => setForm({ ...form, region_id: v,
                              berlaku_sejak: data.hari[v] || form.berlaku_sejak })}>
                      <SelectTrigger id="region_id" data-testid="select-region">
                        <SelectValue placeholder="Pilih wilayah produksi" />
                      </SelectTrigger>
                      <SelectContent>
                        {wilayahPilihan.map((r) => (
                          <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="work_type_id">Jenis pekerjaan</Label>
                    <Select value={form.work_type_id || ""} onValueChange={(v) => setForm({ ...form, work_type_id: v })}>
                      <SelectTrigger id="work_type_id" data-testid="select-work-type">
                        <SelectValue placeholder="Pilih jenis pekerjaan" />
                      </SelectTrigger>
                      <SelectContent>
                        {jenisPilihan.map((w) => (
                          <SelectItem key={w.id} value={String(w.id)}>{w.name || w.code}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {barisForm && (
                <p className="text-sm text-muted-foreground" data-testid="tarif-sekarang-dialog">
                  Tarif sekarang:{" "}
                  <b className="text-foreground">{barisForm.ada ? formatIDR(barisForm.rate_per_pcs_idr) : "belum ada"}</b>
                  {barisForm.versi_berikutnya && (
                    <> · terjadwal {formatIDR(barisForm.versi_berikutnya.rate_per_pcs_idr)} mulai{" "}
                      {formatDate(barisForm.versi_berikutnya.berlaku_sejak)}</>
                  )}
                </p>
              )}

              <div>
                <Label htmlFor="rate">Tarif baru per potong (Rp)</Label>
                <Input id="rate" type="number" min={1} step="1" inputMode="numeric"
                       value={form.rate_per_pcs_idr ?? ""}
                       onChange={(e) => setForm({ ...form, rate_per_pcs_idr: e.target.value })}
                       data-testid="input-rate" />
              </div>

              <div>
                <Label htmlFor="berlaku_sejak">
                  Berlaku sejak{" "}
                  <Hint>Tarif ini dipakai untuk FA yang dibuat mulai tanggal ini sampai ada versi berikutnya. Versi lama tidak diubah maupun dihapus.</Hint>
                </Label>
                <Input id="berlaku_sejak" type="date" min={hariIniForm}
                       value={form.berlaku_sejak ?? hariIniForm}
                       onChange={(e) => setForm({ ...form, berlaku_sejak: e.target.value })}
                       data-testid="input-berlaku-sejak" />
                <p className="text-xs text-muted-foreground mt-1">
                  Tidak boleh mundur dari hari ini di cabang ({formatDate(hariIniForm)}).
                </p>
              </div>

              <div>
                <Label htmlFor="reason">Alasan perubahan</Label>
                <Textarea id="reason" value={form.reason || ""}
                          onChange={(e) => setForm({ ...form, reason: e.target.value })}
                          placeholder="Mis. penyesuaian UMR, kesepakatan baru dengan penjahit…"
                          data-testid="input-reason" />
                <p className="text-xs text-muted-foreground mt-1">
                  Wajib diisi — tampil di riwayat dan jadi bukti bila ada selisih upah.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="langkah-periksa">
              <p className="text-sm font-medium">Periksa sekali lagi sebelum disimpan:</p>
              <p className="text-sm rounded-md border border-border bg-muted/40 p-3" data-testid="ringkasan-ubah-tarif">
                {ringkasan?.kalimat}
              </p>
              {ringkasan?.besar && (
                <div className="rounded-md border border-warning-soft-border bg-warning-soft p-3 text-sm text-warning-soft-foreground"
                     data-testid="peringatan-perubahan-besar">
                  <div className="flex gap-2">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>
                      Perubahannya lebih dari 20%. Pastikan angkanya benar — misalnya tidak kelebihan atau kekurangan
                      satu angka nol.
                    </span>
                  </div>
                  <label className="mt-2 flex items-center gap-2 font-medium">
                    <input type="checkbox" checked={yakin} onChange={(e) => setYakin(e.target.checked)}
                           data-testid="centang-yakin" />
                    Angkanya sudah saya periksa dan benar
                  </label>
                </div>
              )}
              {akanMenungguOwner && (
                <p className="text-sm text-muted-foreground" data-testid="catatan-menunggu-owner">
                  Perubahan ini melewati batas penyesuaian Kepala Cabang, jadi baru berlaku sesudah disetujui Owner.
                  Sampai itu, tarif lama tetap dipakai.
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            {langkah === "ISI" ? (
              <>
                <Button variant="outline" onClick={() => setDialog(null)}>Batal</Button>
                <Button onClick={periksa} data-testid="btn-lanjut-periksa">Lanjut periksa</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setLangkah("ISI")} disabled={saving}>Kembali</Button>
                <Button onClick={simpan} disabled={saving || (ringkasan?.besar && !yakin)}
                        data-testid="simpan-versi-tarif">
                  {saving ? "Menyimpan…" : `Ya, simpan — berlaku sejak ${formatDate(form.berlaku_sejak)}`}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}
    </div>
  );
}
